import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Create axios instance.
//
// HYBRID AUTH (Apr 30 2026): we send BOTH an httpOnly cookie (when the
// browser allows it — same-origin deploys) AND a Bearer header from
// localStorage. The backend's `get_current_user` accepts either, so the
// app works regardless of cross-origin / cookie policies on the deployed
// domain.
//
// Why both? In some deployments (e.g. crm.exoticindia.com fronting a
// different API origin), browsers silently drop the Set-Cookie header
// because of SameSite/cross-site policies. The Bearer fallback guarantees
// authentication survives every production hosting scenario.
//
// XSS risk mitigated separately: every HTML surface rendered with
// dangerouslySetInnerHTML goes through DOMPurify (`sanitizeHtml`), so
// the localStorage token can't be exfiltrated by injected scripts.
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

const TOKEN_KEY = "crm_token";

// Attach Bearer token to every request when present in localStorage.
api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {}
  return config;
});

// On 401, clear the local token so the UI falls back to login.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      try { localStorage.removeItem(TOKEN_KEY); } catch {}
    }
    return Promise.reject(error);
  }
);

// API functions
export const dashboardApi = {
  getStats: (params = {}) => api.get("/dashboard/stats", { params }),
  getPerformance: (params = {}) => api.get("/dashboard/performance", { params }),
  getRecentLeads: (limit = 10) =>
    api.get(`/dashboard/recent-leads?limit=${limit}`),
};

export const leadsApi = {
  getAll: (params = {}) => api.get("/leads", { params }),
  getOne: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post("/leads", data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  refreshMessages: (id) => api.get(`/leads/${id}/refresh-messages`),
};

export const chatsApi = {
  getAll: (params = {}) => api.get("/chats", { params }),
  getNewCount: (since) => api.get("/chats/new-count", { params: since ? { since } : {} }),
  newChat: (payload) => api.post("/chats/new", payload),
};

export const watiApi2 = {
  // Keeping WATI template list out of the existing `watiApi` to avoid touching
  // its many consumers. Used by the "+ New Chat" modal.
  templates: (country = "India") => api.get("/wati/templates", { params: { country } }),
};

export const filtersApi = {
  getOptions: () => api.get("/filters/options"),
};

export const aiApi = {
  suggestReply: (data) => api.post("/ai/suggest-reply", typeof data === "string" ? { lead_id: data } : data),
  extractDetails: (lead_id) => api.post("/ai/extract-details", { lead_id }),
  detectSentiment: (lead_id) => api.post("/ai/detect-sentiment", { lead_id }),
  smartCategorize: (lead_id) => api.post("/ai/smart-categorize", { lead_id }),
  imageSearch: (data) => api.post("/ai/image-search", data),
  triggerLearning: () => api.post("/ai/learn"),
  getKnowledge: () => api.get("/ai/knowledge"),
  getRules: () => api.get("/ai/rules"),
  saveRules: (text) => api.put("/ai/rules", { text }),
};

export const settingsApi = {
  getAssignmentRules: () => api.get("/settings/assignment-rules"),
  updateAssignmentRules: (data) => api.put("/settings/assignment-rules", data),
  getAutoReply: () => api.get("/settings/auto-reply"),
  updateAutoReply: (data) => api.put("/settings/auto-reply", data),
  getPriorityQueue: () => api.get("/settings/priority-queue"),
  updatePriorityQueue: (data) => api.put("/settings/priority-queue", data),
  getCustomRoles: () => api.get("/settings/custom-roles"),
  addCustomRole: (name) => api.post("/settings/custom-roles", { name }),
  deleteCustomRole: (name) => api.delete(`/settings/custom-roles/${encodeURIComponent(name)}`),
  getPagePermissions: () => api.get("/settings/page-permissions"),
  updatePagePermissions: (permissions) => api.put("/settings/page-permissions", { permissions }),
  // Currency exchange — Yash, Apr 29 2026.
  // Rates are INR-anchored ("X-INR"). The team maintains them manually
  // and uses them to convert USD-based product prices into any non-INR
  // currency at the moment a payment link / order is generated.
  getCurrencyRates: () => api.get("/settings/currency-rates"),
  updateCurrencyRates: (rates) => api.put("/settings/currency-rates", { rates }),
  previewCurrencyConversion: (usd_amount, rates) =>
    api.post("/settings/currency-rates/preview", { usd_amount, rates }),
  getRazorpay: () => api.get("/settings/razorpay"),
  updateRazorpay: (data) => api.post("/settings/razorpay", data),
  getTeamNotify: () => api.get("/settings/team-notify"),
  updateTeamNotify: (data) => api.put("/settings/team-notify", data),
};

export const teamNotifyApi = {
  send: (leadId, data) => api.post(`/leads/${leadId}/notify-team`, data),
};

export const agentActivityApi = {
  get: () => api.get("/agent-activity"),
};

export const reportsApi = {
  // Iter 152.22k (May 2026, Yash) — Reports leaderboard "not refreshing"
  // fix. Adds:
  //   • explicit Refresh button (was missing — page reloaded only on date /
  //     filter change which is a non-obvious UX)
  //   • cache-busting `_=Date.now()` query param so any browser/CloudFront/
  //     CDN proxy can't serve stale 200 responses
  //   • Cache-Control: no-cache header on the request side too
  getSummary: (params = {}) => api.get("/reports/summary", {
    params: { ...params, _: Date.now() },
    headers: { "Cache-Control": "no-cache, no-store" },
  }),
  getTicketGraph: (params = {}) => api.get("/reports/ticket-graph", {
    params: { ...params, _: Date.now() },
    headers: { "Cache-Control": "no-cache, no-store" },
  }),
  getPeakHours: (params = {}) => api.get("/reports/peak-hours", {
    params: { ...params, _: Date.now() },
    headers: { "Cache-Control": "no-cache, no-store" },
  }),
  getRepeatContacts: (params = {}) => api.get("/reports/repeat-contacts", {
    params: { ...params, _: Date.now() },
    headers: { "Cache-Control": "no-cache, no-store" },
  }),
  getIssueCategories: (params = {}) => api.get("/reports/issue-categories", {
    params: { ...params, _: Date.now() },
    headers: { "Cache-Control": "no-cache, no-store" },
  }),
  getAiQuality: (params = {}) => api.get("/reports/ai-quality", { params }),
  // Yash, Apr 29 2026 — LLM cost telemetry. Reads from db.llm_usage_log
  // populated by every Claude call (auto-reply, vision, prefill, draft).
  getLlmCost: (params = {}) => api.get("/reports/llm-cost", { params }),
  // Yash, Apr 29 2026 — revenue attributed to AI (online + COD) for the
  // same date range. Pairs with /reports/llm-cost so super-admins can see
  // LLM cost as % of revenue.
  getLlmRevenue: (params = {}) => api.get("/reports/llm-revenue", { params }),
  // Yash, Apr 29 2026 — one-shot historical backfill. Idempotent on the
  // server side (duplicate message_ids are skipped) so the button can be
  // re-clicked safely if it ever times out.
  backfillLlmCost: () => api.post("/reports/llm-cost/backfill", {}, { timeout: 120000 }),
  // Iter 152.21 — sister to backfillLlmCost. Scans historical leads
  // where AI sent the "Order ready to place" email but the
  // sales_address_emailed_at flag wasn't set yet, and writes the flag
  // retroactively. Idempotent.
  // Iter 153.7 (Feb 8, 2026, Yash) — accepts the page's date range so
  // the response's `revenue_after_backfill_in_range` matches what the
  // user sees on the card. Without this, the toast showed the all-time
  // total and the card's date-filtered total looked unchanged → user
  // thought the button was broken.
  backfillLlmRevenueAttribution: (params = {}) =>
    api.post("/reports/llm-revenue/backfill", {}, { params, timeout: 120000 }),
  // Iter 152.22 — read-only snapshot for the "why is the bill $X today"
  // question. Returns env presence, cache hit %, today's spend, top
  // spenders by purpose+model, and killswitch state. Super-admin only.
  fireNowPrewarmer: () => api.post("/admin/llm-cache-prewarmer/fire-now", {}, { timeout: 45000 }),
  setBudgetOverride: (payload) => api.post("/admin/llm-budget-override", payload),
  clearBudgetOverride: () => api.delete("/admin/llm-budget-override"),
  getLlmCacheDiag: () => api.get("/admin/llm-cache-diag"),
  // Iter 153.9 (Feb 8, 2026, Yash) — return the exact prompt cached on
  // Anthropic + per-section breakdown so super_admins can see what's
  // bloated and decide what to trim. Read-only, super_admin gated.
  getLlmCachePrompt: () => api.get("/admin/llm-cache-prompt"),
  getLlmCostHistory: (days = 14) => api.get(`/admin/llm-cost-history?days=${days}`),
};

export const notesApi = {
  get: (leadId) => api.get(`/leads/${leadId}/notes`),
  add: (leadId, content) => api.post(`/leads/${leadId}/notes`, { content }),
  delete: (noteId) => api.delete(`/notes/${noteId}`),
};

export const issueTagsApi = {
  getAll: () => api.get("/issue-tags"),
  addCustom: (tag) => api.post("/issue-tags", { tag }),
  updateLead: (leadId, tags) => api.put(`/leads/${leadId}/issue-tags`, { tags }),
};

export const bulkActionsApi = {
  execute: (lead_ids, action, value) => api.post("/leads/bulk-action", { lead_ids, action, value }),
};

export const escalationApi = {
  escalate: (leadId, reason) => api.post(`/leads/${leadId}/escalate`, { reason }),
  getAll: (status) => api.get("/escalations", { params: { status } }),
  resolve: (escId) => api.put(`/escalations/${escId}/resolve`),
};

export const watiUsApi = {
  status: () => api.get("/wati-us/status"),
  sync: () => api.post("/wati-us/sync"),
};

export const orderTrackingApi = {
  lookup: (orderNumber) => api.get(`/order-tracking/${orderNumber}`),
  detectInChat: (leadId) => api.get(`/leads/${leadId}/detect-orders`),
};

export const booksApi = {
  lookup: (itemcode) => api.get(`/books/${encodeURIComponent(itemcode.trim())}`),
  detectInChat: (leadId) => api.get(`/leads/${leadId}/detect-products`),
};

export const leadActionsApi = {
  reassign: (id, assigned_to) => api.put(`/leads/${id}/reassign`, { assigned_to }),
  updateCustomerDetails: (id, data) => api.put(`/leads/${id}/customer-details`, data),
  placeAdminOrder: (id, data) => api.post(`/leads/${id}/admin-order`, data),
  recordOrder: (id, data) => api.post(`/leads/${id}/record-order`, data),
  chatAction: (id, action) => api.put(`/leads/${id}/chat-action`, { action }),
  sendAttachment: (id, formData) => api.post(`/leads/${id}/send-attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  watiAssign: (id, email) => api.post(`/leads/${id}/wati-assign`, { email }),
  watiStatus: (id, status) => api.post(`/leads/${id}/wati-status`, { status }),
  sendPaymentQR: (id) => api.post(`/leads/${id}/send-payment-qr`),
  updateLabels: (id, labels) => api.put(`/leads/${id}/labels`, { labels }),
  setDepartment: (id, department) => api.put(`/leads/${id}/department`, { department }),
  reroute: (id) => api.post(`/leads/${id}/reroute`),
  aiReplyOnce: (id) => api.post(`/leads/${id}/ai-reply-once`),
  aiToggle: (id, enabled) => api.post(`/leads/${id}/ai-toggle`, { enabled }),
  // ─── Iter 153.43 (Feb 9, 2026, Yash) — Bulk / Export / Share ────
  bulkAssign: (leadIds, assignedTo) =>
    api.post(`/leads/bulk-assign`, { lead_ids: leadIds, assigned_to: assignedTo }),
  exportChat: (id, fmt = 'csv') =>
    api.get(`/leads/${id}/export?format=${fmt}`, { responseType: 'blob' }),
  createShare: (id, expiresInDays = 7, includeInternalNotes = false) =>
    api.post(`/leads/${id}/share`, {
      expires_in_days: expiresInDays,
      include_internal_notes: includeInternalNotes,
    }),
  listShares: (id) => api.get(`/leads/${id}/shares`),
  revokeShare: (id, shareId) => api.delete(`/leads/${id}/share/${shareId}`),
  emailExport: (id, payload) => api.post(`/leads/${id}/share/email`, payload),
};

export const adminApi = {
  rerouteAllOpen: () => api.post(`/admin/reroute-all-open`),
  reclassifyBroadcasts: () => api.post(`/admin/reclassify-broadcasts`),
  repairMedia: (limit = 200) => api.post(`/admin/wati/repair-media?limit=${limit}`),
  listDuplicateLeads: () => api.get(`/admin/leads/duplicates`),
  mergeDuplicateLeads: (payload = {}) => api.post(`/admin/leads/merge-duplicates`, payload),
  auditMisroutedCs: (limit = 500, scanCount = 5) =>
    api.get(`/admin/audit/misrouted-cs-leads`, { params: { limit, scan_count: scanCount } }),
  revertMisroutedCs: (leadIds) =>
    api.post(`/admin/audit/misrouted-cs-leads/revert`, { lead_ids: leadIds }),
  // Iter 153.23 (Feb 8 2026, Yash) — AI auto-placed orders ledger.
  // Surfaces every admin order the AI placed so the team can corroborate
  // at month-end. Super admin only.
  listAiPlacedOrders: (params = {}) => api.get(`/admin/ai-placed-orders`, {
    params: { ...params, _: Date.now() },
    headers: { "Cache-Control": "no-cache, no-store" },
  }),
  exportAiPlacedOrdersCsv: (params = {}) => api.get(`/admin/ai-placed-orders/export`, {
    params,
    responseType: "blob",
  }),
  // Iter 156.34 (Feb 28 2026, Yash) — temporary cancel-call audit log.
  // Surfaces every attempt to call admin_crm.php?action=order&subaction=cancel
  // with HTTP status + response body so operators can debug missing
  // upstream requests. To be removed once root cause is identified.
  getCancelUpstreamLogs: (params = {}) => api.get(`/admin/cancel-upstream-logs`, {
    params: { ...params, _: Date.now() },
    headers: { "Cache-Control": "no-cache, no-store" },
  }),
  clearCancelUpstreamLogs: (olderThanHours = 0) =>
    api.delete(`/admin/cancel-upstream-logs`, { params: { older_than_hours: olderThanHours } }),
};

export const labelsApi = {
  getAll: () => api.get("/labels"),
};

export const adminOrdersApi = {
  getAll: (params = {}) => api.get("/admin-orders", { params }),
  getOne: (id) => api.get(`/admin-orders/${id}`),
  create: (data) => api.post("/admin-orders", data),
  update: (id, data) => api.put(`/admin-orders/${id}`, data),
  delete: (id) => api.delete(`/admin-orders/${id}`),
};

export const proformaApi = {
  getAll: (params = {}) => api.get("/proforma-invoices", { params }),
  getOne: (id) => api.get(`/proforma-invoices/${id}`),
  create: (data) => api.post("/proforma-invoices", data),
  update: (id, data) => api.put(`/proforma-invoices/${id}`, data),
  delete: (id) => api.delete(`/proforma-invoices/${id}`),
  convertToOrder: (id) => api.post(`/proforma-invoices/${id}/convert-to-order`),
  downloadPdf: (id) => api.get(`/proforma-invoices/${id}/pdf`, { responseType: 'blob' }),
};

export const bankDetailsApi = {
  getAll: () => api.get("/bank-details"),
  create: (data) => api.post("/bank-details", data),
  update: (id, data) => api.put(`/bank-details/${id}`, data),
  delete: (id) => api.delete(`/bank-details/${id}`),
};

export const companyApi = {
  getSettings: () => api.get("/company-settings"),
  updateSettings: (data) => api.put("/company-settings", data),
};

export const paymentApi = {
  createLink: (data) => api.post("/payment-links", data),
  getLinks: (leadId) => api.get(`/payment-links/${leadId}`),
  getQR: (linkId) => api.get(`/payment-links/qr/${linkId}`),
  sendQR: (linkId) => api.post(`/payment-links/${linkId}/send-qr`),
  // Stripe — overseas card payments. The frontend ALWAYS sends the
  // window.location.origin so the backend can build success/cancel URLs
  // that route back to the CRM (per platform security rule). Amount and
  // currency are still validated server-side.
  createStripeLink: (data) => api.post("/payment-links/stripe", data),
  getStripeStatus: (sessionId) => api.get(`/payment-links/stripe/status/${sessionId}`),
  // PayPal — Orders v2 (Yash, Apr 28 2026). Same shape as Razorpay/Stripe
  // so the chat modal stays uniform. Backend creates a PayPal Order with
  // intent=CAPTURE and returns the `approve` URL the customer follows on
  // PayPal-hosted checkout. Webhook at /api/webhook/paypal marks it paid.
  createPaypalLink: (data) => api.post("/payment-links/paypal", data),
  // Provider feature-flag endpoint — frontend modal calls this on mount
  // so disabled providers can be greyed out instead of failing on click.
  getProvidersConfig: () => api.get("/payment-providers/config"),
};

// Yash, Apr 28 2026 — Exotic India website orders API wrapper.
// Auto-fetch a lead's order history from exoticindia.com (admin_crm.php)
// + manual lookup by orderid/email/mobile + place new admin orders.
export const websiteOrdersApi = {
  byLead: (leadId) => api.get(`/website-orders/by-lead/${leadId}`),
  lookup: ({ orderid, email, mobile }) => {
    const p = {};
    if (orderid) p.orderid = orderid;
    if (email) p.email = email;
    if (mobile) p.mobile = mobile;
    return api.get(`/website-orders/lookup`, { params: p });
  },
  // Iter 156.59 — explicit 40s client timeout so a stalled upstream fails
  // fast with a toast instead of spinning forever (prod incident: the
  // admin-order form hung indefinitely when the catalog upstream was down).
  // Backend preflight is now bounded (~8s) + create (~12s) + optional coupon.
  create: (data) => api.post(`/website-orders/create`, data, { timeout: 40000 }),
  // Yash, Apr 29 2026 — one-shot AI prefill from chat. Pulls name, address,
  // items, txn id (Razorpay/Stripe/PayPal webhook OR Claude Vision OCR on
  // payment screenshots), currency, and a "things our team should know"
  // narration. Returns per-field confidence so the UI can flag what to verify.
  prefillFromChat: (leadId) => api.post(`/website-orders/prefill-from-chat/${leadId}`),
  // Yash, Apr 29 2026 — ad-hoc coupon (right-panel widget). The order
  // form auto-creates its own coupon under the hood; this endpoint is for
  // standalone "create coupon for this customer" use-cases.
  createCoupon: (data) => api.post(`/website-orders/coupon`, data),
  // Iter 151 (Feb 2 2026) — item-wise cancel + refund (India, ≤ ₹1500 rollout).
  listCancelReasons: () => api.get(`/website-orders/cancellation-reasons`),
  cancelItem: (orderId, itemcode, payload) =>
    api.post(`/website-orders/${orderId}/items/${encodeURIComponent(itemcode)}/cancel`, payload),
  sendCancellationTestEmail: () => api.post(`/website-orders/cancellation-test-email`),
  // Iter 151.1 — Super-admin dashboard widget ("Why customers leave").
  cancellationSummary: (days = 7) => api.get(`/analytics/cancellation-summary`, { params: { days } }),
};


export const broadcastsApi = {
  getAll: (params = {}) => api.get("/broadcasts", { params }),
};

export const messagesApi = {
  create: (data) => api.post("/messages", data),
  sendWhatsApp: (phone, message) => api.post("/whatsapp/send", { phone, message }),
  resend: (msgId) => api.post(`/messages/${msgId}/resend`),
  verifyPayment: (msgId) => api.post(`/messages/${msgId}/verify-payment`),
};

export const quickRepliesApi = {
  getAll: () => api.get("/quick-replies"),
  create: (data) => api.post("/quick-replies", data),
  update: (id, data) => api.put(`/quick-replies/${id}`, data),
  delete: (id) => api.delete(`/quick-replies/${id}`),
};

export const translateApi = {
  translate: (text, targetLanguage = "English") =>
    api.post("/ai/translate", { text, target_language: targetLanguage }),
};

// Telecmi (PIOPIY) calling integration. Status, click-to-call, agent mapping.
export const telecmiApi = {
  status: () => api.get("/telecmi/status"),
  connectivity: () => api.get("/telecmi/connectivity"),
  // Sprint 3: Live call (transcription + coaching + controls)
  liveStart: (payload) => api.post("/telecmi/live/start", payload),
  liveGet: (callId) => api.get(`/telecmi/live/${callId}`),
  liveActiveByPhone: (phone) => api.get(`/telecmi/live/active/by-phone/${encodeURIComponent(phone)}`),
  liveAppend: (callId, payload) => api.post(`/telecmi/live/${callId}/append-transcript`, payload),
  liveCoach: (callId) => api.post(`/telecmi/live/${callId}/coach`),
  liveMute: (callId) => api.post(`/telecmi/live/${callId}/mute`),
  liveUnmute: (callId) => api.post(`/telecmi/live/${callId}/unmute`),
  liveHold: (callId) => api.post(`/telecmi/live/${callId}/hold`),
  liveResume: (callId) => api.post(`/telecmi/live/${callId}/resume`),
  liveTransfer: (callId, payload) => api.post(`/telecmi/live/${callId}/transfer`, payload),
  liveEnd: (callId) => api.post(`/telecmi/live/${callId}/end`),
  liveMockScript: (callId, payload) => api.post(`/telecmi/live/${callId}/mock-script`, payload),
  testCall: (toNumber, agentExtension) =>
    api.post("/telecmi/test-call", { to_number: toNumber, agent_extension: agentExtension }),
  clickToCall: (customerPhone, leadId) =>
    api.post("/telecmi/click-to-call", { customer_phone: customerPhone, lead_id: leadId }),
  listCalls: (params = {}) => api.get("/telecmi/calls", { params }),
  listAgents: () => api.get("/telecmi/agents"),
  upsertAgent: (data) => api.post("/telecmi/agents", data),
  deleteAgent: (userId) => api.delete(`/telecmi/agents/${userId}`),
  customer360: (phone) => api.get(`/telecmi/customer-360/${encodeURIComponent(phone)}`),
  customer360Full: (phone) => api.get(`/customer-360/${encodeURIComponent(phone)}/full`),
  activeIncoming: () => api.get("/telecmi/active-incoming"),
  analytics: (days = 7) => api.get(`/telecmi/analytics?days=${days}`),
  generateSummary: (callId) => api.post(`/telecmi/calls/${callId}/ai-summary`),
  // Sprint 2
  wallboard: () => api.get("/telecmi/wallboard"),
  getQueue: (onlyMine = false) => api.get(`/telecmi/outbound-queue?only_mine=${onlyMine}`),
  addQueue: (data) => api.post("/telecmi/outbound-queue", data),
  bulkQueue: (items) => api.post("/telecmi/outbound-queue/bulk", items),
  completeQueue: (id) => api.post(`/telecmi/outbound-queue/${id}/complete`),
  removeQueue: (id) => api.delete(`/telecmi/outbound-queue/${id}`),
  getCallbacks: () => api.get("/telecmi/callbacks"),
  addCallback: (data) => api.post("/telecmi/callbacks", data),
  completeCallback: (id) => api.post(`/telecmi/callbacks/${id}/complete`),
  removeCallback: (id) => api.delete(`/telecmi/callbacks/${id}`),
  listVoicemails: () => api.get("/telecmi/voicemails"),
  createVoicemail: (data) => api.post("/telecmi/voicemails", data),
  removeVoicemail: (id) => api.delete(`/telecmi/voicemails/${id}`),
  dropVoicemail: (callId, vmId) => api.post(`/telecmi/calls/${callId}/drop-voicemail`, { voicemail_id: vmId }),
  qualityScore: (callId) => api.post(`/telecmi/calls/${callId}/quality-score`),
};

export const ordersApi = {
  getAll: (params = {}) => api.get("/orders", { params }),
  create: (data) => api.post("/orders", data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
};

export const remindersApi = {
  getAll: (upcoming = false) => api.get(`/reminders?upcoming=${upcoming}`),
  create: (data) => api.post("/reminders", data),
  complete: (id) => api.put(`/reminders/${id}/complete`),
  delete: (id) => api.delete(`/reminders/${id}`),
};

export const usersApi = {
  getAll: (opts = {}) => {
    // Admin management screens pass { includeInactive: true } to see soft-deleted
    // users (so they can reactivate). Everyone else (assign pickers, chat list,
    // calls page, lead detail) gets the clean active-only default from backend.
    const qs = opts.includeInactive ? "?include_inactive=true" : "";
    return api.get(`/users${qs}`);
  },
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  promoteSuperAdmin: (id) => api.post(`/users/${id}/promote-super-admin`),
  demoteToAdmin: (id) => api.post(`/users/${id}/demote-to-admin`),
  resetPassword: (id, password) => api.post(`/users/${id}/reset-password`, { password }),
};

export const exportApi = {
  leads: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/export/leads${q ? '?' + q : ''}`, { responseType: 'blob' });
  },
  orders: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/export/orders${q ? '?' + q : ''}`, { responseType: 'blob' });
  },
  performance: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/export/performance${q ? '?' + q : ''}`, { responseType: 'blob' });
  },
};

export const analyticsApi = {
  getDashboard: (params = {}) => api.get("/dashboard/analytics", { params }),
  getLeaderboard: (params = {}) => api.get("/dashboard/leaderboard", { params }),
  getSlaBreaches: () => api.get("/dashboard/sla-breaches"),
  getCategoryIntent: (period = "7d") => api.get("/dashboard/category-intent", { params: { period } }),
};

export const slaApi = {
  getConfig: () => api.get("/settings/sla"),
  updateConfig: (data) => api.put("/settings/sla", data),
};

export const scheduledMessagesApi = {
  getAll: () => api.get("/scheduled-messages"),
  create: (data) => api.post("/scheduled-messages", data),
  delete: (id) => api.delete(`/scheduled-messages/${id}`),
};

export const watiApi = {
  sync: () => api.post("/wati/sync"),
  syncMessages: () => api.post("/wati/sync-messages"),
  getSyncStatus: () => api.get("/wati/sync-status"),
  getStatus: () => api.get("/wati/status"),
  importContact: (phone) => api.post(`/wati/import-contact?phone=${encodeURIComponent(phone)}`),
  refreshAllStatuses: (country = "all") => api.post(`/wati/refresh-all-statuses?country=${country}`),
  refreshStatusProgress: (jobId) => api.get(`/wati/refresh-status-progress/${jobId}`),
  mergeDuplicateLeads: (dryRun = false) => api.post(`/leads/merge-duplicates?dry_run=${dryRun}`),
};

// Zoho Desk integration (email ticketing)
export const zohoApi = {
  getStatus: () => api.get("/zoho/status"),
  getAuthorizeUrl: () => api.get("/zoho/authorize-url"),
  exchangeCode: (code) => api.post("/zoho/exchange-code", { code }),
  ticketsByContact: ({ email, phone, limit = 20 } = {}) => {
    const params = new URLSearchParams();
    if (email) params.set("email", email);
    if (phone) params.set("phone", phone);
    if (limit) params.set("limit", limit);
    return api.get(`/zoho/tickets-by-contact?${params}`);
  },
  listTickets: ({ departmentId, status, assigneeId, viewId, starredOnly, search, from = 0, limit = 100, sortBy = "-modifiedTime" } = {}) => {
    const params = new URLSearchParams();
    if (departmentId) params.set("department_id", departmentId);
    if (status) params.set("status", status);
    if (assigneeId) params.set("assignee_id", assigneeId);
    if (viewId) params.set("view_id", viewId);
    if (starredOnly) params.set("starred_only", "true");
    if (search) params.set("search", search);
    params.set("from", from);
    params.set("limit", limit);
    params.set("sort_by", sortBy);
    return api.get(`/zoho/tickets?${params}`);
  },
  ticketDetails: (id) => api.get(`/zoho/tickets/${id}`),
  ticketThreads: (id, limit = 30) => api.get(`/zoho/tickets/${id}/threads?limit=${limit}`),
  ticketAttachments: (id) => api.get(`/zoho/tickets/${id}/attachments`),
  ticketHistory: (id) => api.get(`/zoho/tickets/${id}/history`),
  ticketResolution: (id) => api.get(`/zoho/tickets/${id}/resolution`),
  ticketTimeEntries: (id) => api.get(`/zoho/tickets/${id}/timeEntry`),
  // Iter 143 — private/internal comments (separate from /threads which is customer-facing email only)
  ticketComments: (id) => api.get(`/zoho/tickets/${id}/comments`),
  createTicketComment: (id, content, isPrivate = true) =>
    api.post(`/zoho/tickets/${id}/comments`, { content, is_private: isPrivate }),
  updateTicketStatus: (id, status) => api.patch(`/zoho/tickets/${id}/status`, { status }),
  replyToTicket: (id, payload = {}) => api.post(`/zoho/tickets/${id}/reply`, payload),
  createTicket: (payload) => api.post("/zoho/tickets/create", payload),
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/zoho/tickets/${id}/upload-attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  // New features
  listAgents: () => api.get("/zoho/agents"),
  listViews: () => api.get("/zoho/views"),
  unreadCount: () => api.get("/zoho/unread-count"),
  stats: () => api.get("/zoho/stats"),
  reassignTicket: (id, assigneeId) => api.patch(`/zoho/tickets/${id}/assignee`, { assigneeId }),
  starTicket: (id, note = "") => api.post(`/zoho/tickets/${id}/star`, { note }),
  unstarTicket: (id) => api.delete(`/zoho/tickets/${id}/star`),
  getStarred: () => api.get("/zoho/starred"),
  linkLead: (ticketId, leadId, source = "manual") => api.post(`/zoho/tickets/${ticketId}/link-lead`, { lead_id: leadId, source }),
  unlinkLead: (ticketId, leadId) => api.delete(`/zoho/tickets/${ticketId}/link-lead/${leadId}`),
  linkedLeads: (ticketId) => api.get(`/zoho/tickets/${ticketId}/linked-leads`),
  suggestLeads: (ticketId) => api.get(`/zoho/tickets/${ticketId}/suggest-leads`),
  autoLinkTicket: (ticketId) => api.post(`/zoho/tickets/${ticketId}/auto-link`),
  ticketsForLead: (leadId) => api.get(`/leads/${leadId}/zoho-tickets`),
  aiDraftReply: (ticketId, { instruction, tone = "professional" } = {}) =>
    api.post(`/zoho/tickets/${ticketId}/ai-draft`, { instruction, tone }),
  listSavedViews: () => api.get("/zoho/saved-views"),
  saveView: (payload) => api.post("/zoho/saved-views", payload),
  deleteView: (id) => api.delete(`/zoho/saved-views/${id}`),
};

export const ragApi = {
  status: () => api.get("/ai/rag/status"),
  ingest: (months = 12) => api.post(`/ai/rag/ingest-zoho?months=${months}`),
  rebuild: () => api.post("/ai/rag/rebuild-index"),
};

export const customersApi = {
  search: (params = {}) => api.get("/customers", { params }),
  detail: (id) => api.get(`/customers/${id}`),
  timeline: (id) => api.get(`/customers/${id}/timeline`),
  migrateFromLeads: () => api.post("/customers/migrate/from-leads"),
  migrateFromZoho: () => api.post("/customers/migrate/from-zoho"),
  migrateFromWatiContacts: (account = "both") => api.post(`/customers/migrate/from-wati-contacts?account=${account}`),
  migrationStatus: () => api.get("/customers/migrate/status"),
};

export const pushApi = {
  vapidKey: () => api.get("/push/vapid-public-key"),
  subscribe: (subscription) => api.post("/push/subscribe", subscription),
  unsubscribe: (endpoint) => api.post("/push/unsubscribe", { endpoint }),
  listMine: () => api.get("/push/subscriptions"),
  test: () => api.post("/push/test"),
};

/**
 * Iter 139 — bulk AI toggle on all OPEN chats. Used on the Settings AI card
 * for sale-day / campaign-blast situations where the team wants to flip the
 * whole room over to AI for a few hours.
 */
export const aiBulkApi = {
  enableAll: (reason = "manual") =>
    api.post("/leads/ai-bulk-enable", { reason }),
  disableAll: () => api.post("/leads/ai-bulk-disable"),
};

/**
 * Proactive campaigns — abandoned-cart / enquiry-no-reply (Iter 136, Feb 2026).
 *
 * Super-admin-only writes. The preview & list endpoints are open to
 * any authenticated user so leaders/admins can verify the segment
 * before it's enabled.
 */
export const campaignsApi = {
  previewSegment: (params) => api.get("/campaigns/segments/preview", { params }),
  list: () => api.get("/campaigns"),
  create: (data) => api.post("/campaigns", data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  remove: (id) => api.delete(`/campaigns/${id}`),
  runNow: (id) => api.post(`/campaigns/${id}/run-now`),
  results: (id, days = 30) => api.get(`/campaigns/${id}/results`, { params: { days } }),
  messages: (campaignId, limit = 100) =>
    api.get("/campaigns/messages", { params: { campaign_id: campaignId, limit } }),
  // Iter 137 — list registered base templates so the form can autocomplete.
  registeredTemplates: () => api.get("/campaigns/templates/registered"),
  // Iter 138 — dashboard widget summary across all campaigns.
  recoveredSummary: (days = 30) => api.get("/campaigns/recovered-summary", { params: { days } }),
};

export const ticketDuplicatesApi = {
  list: (windowHours = 48, limit = 50) =>
    api.get("/tickets/duplicates", { params: { window_hours: windowHours, limit } }),
  merge: (key, primaryId, mergedIds) =>
    api.post("/tickets/duplicates/merge", { key, primary_id: primaryId, merged_ids: mergedIds }),
};

export default api;
